import {
  alpha,
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { convetToTitleCase, enqueueSnackbar, truncateText, useActiveLink, useUserAccessStore } from '@zeta-core';
import { PlusIcon, ZetaAiIcon } from '@zeta-core/assets';
import {
  IAdvisorUser,
  IClientCardItem,
  IGetFinancialPlanTypesRequest,
  IGetFinancialPlanTypesResponse,
  IGenericErrorResponse,
  IPersonalPiiDetails,
  IViewClientDashboardRequest,
  IViewClientDashboardResponse,
  IViewFactFindPersonalDetailsRequest,
  IViewFactFindPersonalDetailsResponse,
} from '@zeta-types';
import { RouterLinks } from '../../app-route-paths';
import { DASHBOARD_CONFIG, MANAGED_CLIENT_NAVIGATION } from '../common/constants';

import { styled } from '@mui/material/styles';
import { useMutation } from '@tanstack/react-query';
import { getClientFeatureAccess } from '@zeta-commons';
import { AdvisorAPIs, FinancialPlanningService, getErrorMessage, InvestorService, QueryAPIs } from '@zeta-services';
import { AxiosResponse } from 'axios';
import { AddUserAccessModal } from '../../pages/dashboard/client-info/discovery/components/add-user-access-modal';
import { AdvisorAccessInfo } from '../../pages/dashboard/client-info/discovery/components/advisor-access-info';
import { ManageUserAccessModal } from '../../pages/dashboard/client-info/discovery/components/manage-user-access-modal';

const StyledIcon = styled('div')<{ size: string }>(({ theme, size }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: size,
  height: size,
}));

type StyledDotIconProps = { active?: boolean };

export const StyledDotIcon = styled('span')<StyledDotIconProps>(({ active, theme }) => ({
  width: 4,
  height: 4,
  borderRadius: '50%',
  padding: '0px',
  backgroundColor: theme.palette.text.disabled,
  transition: theme.transitions.create(['transform'], { duration: theme.transitions.duration.shorter }),
  ...(active && { transform: 'scale(2)', backgroundColor: theme.palette.primary.main }),
}));

export const StyledLine = styled('span')<StyledDotIconProps>(({ active, theme }) => ({
  width: '2px',
  height: '40px',
  position: 'absolute',
  backgroundColor: alpha(theme.palette.primary.main, 0.16),
  transition: theme.transitions.create(['transform'], { duration: theme.transitions.duration.shorter }),
  ...(active && { transform: 'scale(2)', backgroundColor: theme.palette.primary.main }),
}));

interface Props {
  messageCount: number;
  advisorUserList: IAdvisorUser[];
  setAdvisorUserList: (advisorUserList: IAdvisorUser[]) => void;
}

const ManagedClientNavigation = ({ messageCount, advisorUserList, setAdvisorUserList }: Props) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { pathname, state } = useLocation();
  const locationState = (state || {}) as { entityData: IClientCardItem; clientData: IClientCardItem };

  const [clientRegistered, setClientRegistered] = useState<boolean>(false);
  const [clientCommunicationEnabled, setClientCommunicationEnabled] = useState(false);

  const planningStatus = locationState?.clientData?.planningStatus;
  const userAccess = useUserAccessStore((state) => state.userAccess);
  const isAiAccessible = getClientFeatureAccess((userAccess as any)?.access, 'Advisor-Client-AI', 'Managed').hasAccess;

  const [expanded, setExpanded] = useState<string | null>(null);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [clientData, setClientData] = useState<IPersonalPiiDetails | undefined>();
  const [planDetails, setPlanDetails] = useState<any>({});
  const [showAddAccess, setShowAddAccess] = useState(false);
  const [showManageAccess, setShowManageAccess] = useState(false);

  const getClientData = useMutation({
    mutationFn: (payload: IViewClientDashboardRequest) => QueryAPIs().clientDashboard(payload),
    onSuccess: (response: AxiosResponse<IViewClientDashboardResponse>) => {
      const data = (response?.data?.clientCardItems?.[0] || {}) as any;
      setClientRegistered(data?.clientRegistered || data?.partnerClientRegistered);
    },
    onError: (error: IGenericErrorResponse) => {
      const errorCode = (error as any)?.response?.data?.errorCode || error?.response?.data?.error?.errorCode || 'INTERNAL_SERVER_ERROR';
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  const getClientCommunicationConfig = useMutation({
    mutationFn: () => AdvisorAPIs().getSettingsConfigData({}),
    onSuccess: (response) => {
      setClientCommunicationEnabled(Boolean(response?.data?.isEmailFlowEnabled));
    },
    onError: () => {
      setClientCommunicationEnabled(false);
    },
  });

  useEffect(() => {
    getClientData.mutateAsync({ clientName: locationState?.clientData?.retailClientName } as any);
    getClientCommunicationConfig.mutateAsync();
  }, []);

  const handleShowAddAccess = () => {
    setShowAddAccess(!showAddAccess);
  };

  const handleShowManageAccess = () => {
    setShowManageAccess(!showManageAccess);
  };

  const handleExpandClick = (path: string, index: number) => {
    if (expanded === path && index === expandedIndex) {
      setExpanded(null);
      setExpandedIndex(null);
    } else {
      setExpanded(path);
      setExpandedIndex(index);
    }
  };

  const renderNavItems = (items: any, level = 0) => {
    return items.map((item: any, index: any) => {
      const hasChildren = Array.isArray(item.children) && item.children.length > 0;
      const isExpanded = expanded === item.path && expandedIndex === index;
      return (
        item.show && (
          <Box key={item.path}>
            <Tooltip title={item.disabled ? item.tooltip : item.title} placement="right" arrow>
              <ListItem disableGutters disablePadding>
                <ListItemButton
                  onClick={() => (hasChildren ? handleExpandClick(item.path, index) : navigate(item.path, { state: locationState }))}
                  sx={{
                    borderRadius: 1,
                    bgcolor: pathname.includes(item.path) || isExpanded ? alpha(theme.palette.primary.main, 0.16) : 'inherit',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
                    '& .MuiTouchRipple-child': { bgcolor: `${theme.palette.primary.main} !important` },
                  }}
                  disabled={item.disabled}
                >
                  <ListItemIcon sx={{ color: theme.palette.primary.main }}>
                    <item.icon />
                  </ListItemIcon>

                  <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                    <ListItemText>{item.title}</ListItemText>
                    {item?.name === 'secureMessages' && (
                      <ListItemText>{messageCount > 0 && <Badge badgeContent={messageCount} color="secondary" sx={{ display: 'block' }} />}</ListItemText>
                    )}
                  </Stack>
                  {hasChildren && (
                    <IconButton
                      sx={{ color: theme.palette.primary.main, padding: 0, minWidth: 0 }}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent triggering the ListItemButton click
                        handleExpandClick(item.path, index);
                      }}
                    >
                      <PlusIcon color={isExpanded ? theme.palette.primary.main : theme.palette.grey[500]} />
                    </IconButton>
                  )}
                </ListItemButton>
              </ListItem>
            </Tooltip>

            {/* Render children recursively if present */}
            {hasChildren && (
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <List disablePadding>
                  {item?.children?.map((childItem: any) => {
                    // Check if the child item is active
                    const isChildActive = useActiveLink(childItem.path, false);
                    const show = Object.keys(planDetails || {})?.includes(childItem.name) ? (planDetails as any)?.[childItem.name] : true;

                    return (
                      show && (
                        <ListItem key={childItem.path} disableGutters disablePadding>
                          <ListItemButton
                            onClick={() => navigate(childItem.path, { state: locationState })}
                            sx={{
                              borderRadius: 1,
                              bgcolor: isChildActive
                                ? alpha(theme.palette.primary.main, 0.16) // Active background for child
                                : 'inherit',
                              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
                              '& .MuiTouchRipple-child': { bgcolor: `${theme.palette.primary.main} !important` },
                            }}
                          >
                            {/* Render dot or line for child items */}
                            <StyledIcon size="8px" sx={{ mx: '10px' }}>
                              {isChildActive ? <StyledDotIcon active={isChildActive} /> : <StyledLine />}
                            </StyledIcon>
                            <ListItemIcon sx={{ color: isChildActive ? theme.palette.primary.main : 'text.secondary' }}>
                              <childItem.icon />
                            </ListItemIcon>

                            <ListItemText sx={{ color: isChildActive ? 'text.main' : 'text.secondary' }}>{childItem.title}</ListItemText>
                          </ListItemButton>
                        </ListItem>
                      )
                    );
                  })}
                </List>
              </Collapse>
            )}
          </Box>
        )
      );
    });
  };

  const getClientDetails = useMutation({
    mutationFn: (payload: IViewFactFindPersonalDetailsRequest) => InvestorService().viewPersonalDetails(payload),

    onSuccess: (response: AxiosResponse<IViewFactFindPersonalDetailsResponse>) => {
      setClientData(response?.data?.clientDetails?.personalPiiDetails);
    },
    onError: (error: IGenericErrorResponse) => {
      const errorCode = (error as any)?.response?.data?.errorCode || error?.response?.data?.error?.errorCode || 'INTERNAL_SERVER_ERROR';
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  const getFinancialPlanTypes = useMutation({
    mutationFn: (payload: IGetFinancialPlanTypesRequest) => FinancialPlanningService().getFinancialPlanTypes(payload),

    onSuccess: (response: AxiosResponse<IGetFinancialPlanTypesResponse>) => {
      setPlanDetails(response?.data);
    },
    onError: (error: IGenericErrorResponse) => {
      const errorCode = (error as any)?.response?.data?.errorCode || error?.response?.data?.error?.errorCode || 'INTERNAL_SERVER_ERROR';
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  useEffect(() => {
    getClientDetails.mutateAsync({ clientID: locationState?.clientData?.clientID });
    getFinancialPlanTypes.mutateAsync({ clientId: locationState?.clientData?.clientID, caseId: locationState?.clientData?.caseID });
  }, [locationState]);

  return (
    <Stack
      sx={{
        maxWidth: DASHBOARD_CONFIG.NAVIGATION.DESKTOP_WIDTH,
        width: '100%',
        position: 'fixed',
        height: `calc(100vh - ${DASHBOARD_CONFIG.HEADER.DESKTOP_HEIGHT}px)`,
        borderRight: `1px solid ${theme.palette.divider}`,
        zIndex: theme.zIndex.appBar + 3,
        bgcolor: 'background.paper',
      }}
    >
      <Stack
        sx={{
          p: 3,
          px: 3,
          overflowY: 'auto',
          height: '100%',
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="column" sx={{ gap: 1, p: 2, border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`, borderRadius: 1, mb: 2 }}>
          <Box sx={{ p: 1.5 }}>
            <Tooltip title={`${convetToTitleCase(clientData?.title)} ${clientData?.firstName} ${clientData?.lastName}`}>
              <Typography variant="h6">
                {truncateText(`${convetToTitleCase(clientData?.title)} ${clientData?.firstName} ${clientData?.lastName}`, 20)}
              </Typography>
            </Tooltip>

            <>
              <Typography variant="body2" color="text.secondary">
                Email: {clientData?.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Phone: {clientData?.mobileNumber}
              </Typography>
            </>
          </Box>

          <Divider sx={{ borderStyle: 'dashed', my: 1, borderColor: alpha(theme.palette.grey[500], 0.8) }} />

          <AdvisorAccessInfo
            canWrite={false}
            handleShowManageAccess={handleShowManageAccess}
            handleShowAddAccess={handleShowAddAccess}
            advisorUserList={advisorUserList as any}
            setAdvisorUserList={setAdvisorUserList as any}
            handleClosePopover={() => undefined}
            open={false}
            anchorEl={null}
          />
        </Stack>

        <Stack marginBottom={10}>
          <List disablePadding>
            {renderNavItems(MANAGED_CLIENT_NAVIGATION(clientRegistered, planningStatus, userAccess as any, clientCommunicationEnabled))}
          </List>
        </Stack>

        <Box sx={{ mt: 'auto', mb: 6, position: 'absolute', bottom: 0 }}>
          {isAiAccessible && (
            <Button
              startIcon={<ZetaAiIcon size="20px" />}
              onClick={() => navigate(RouterLinks.askAi, { state: locationState })}
              sx={{
                width: 'max-content',
                border: `2px solid ${theme.palette.primary.main} !important`,
                boxShadow: `${theme.customShadows.primary} !important`,
              }}
              variant="outlined"
              size="large"
            >
              Ask Zeta
            </Button>
          )}
        </Box>
      </Stack>

      <AddUserAccessModal open={showAddAccess} onClose={() => handleShowAddAccess()} setAdvisorUserList={setAdvisorUserList} />
      <ManageUserAccessModal
        open={showManageAccess}
        onClose={() => handleShowManageAccess()}
        handleShowAddAccess={handleShowAddAccess}
        userList={advisorUserList}
        setAdvisorUserList={setAdvisorUserList}
        isPending={false}
        canWrite={false}
      />
    </Stack>
  );
};

export default ManagedClientNavigation;
